import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar, X, Info, CaretLeft, CaretRight } from '@phosphor-icons/react';
import menuService from '../services/menuService';
import useDatabase from '../hooks/useDatabase';
import DatePicker from '../components/DatePicker';
import { useAuth } from '../hooks/useAuth';

const Cardapio = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dailyMenu, setDailyMenu] = useState([]);
  const [weeklyMenu, setWeeklyMenu] = useState({});
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0); // 0 = current week, -1 = previous week, 1 = next week
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'weekly'
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [loadingWeeklyMenu, setLoadingWeeklyMenu] = useState(false);
  const [menuError, setMenuError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [searchTerm] = useState('');
  const [selectedCategory] = useState('all');

  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  
  // Database connection hook
  const { isConnected } = useDatabase();
  // Auth hook to obtain logged user unit
  const { currentUser } = useAuth();

  // Avoid duplicate fetches on mount (React.StrictMode) and connection flips
  const hasFetchedRef = useRef(false);

  // Load daily menu from database
  const loadDailyMenu = useCallback(async (showLoading = true, targetDate = null) => {
    try {
      if (showLoading) setLoadingMenu(true);
      setMenuError(null);
      
      // Use provided date or current date
      const dateToUse = targetDate || currentDate;
      const menuData = await menuService.getDailyMenu(dateToUse);
      setDailyMenu(menuData);
      setRetryCount(0);
    } catch (error) {
      // console.error('Error loading daily menu:', error);
      setMenuError(error.message || 'Erro ao carregar cardápio');
      
      // Use empty menu if database fails
      setDailyMenu([]);
    } finally {
      if (showLoading) setLoadingMenu(false);
    }
  }, [currentDate]);

  // Load weekly menu (rotation) and augment with items of same day
  const loadWeeklyMenu = useCallback(async (showLoading = true, weekOffset = 0) => {
    try {
      if (showLoading) {
        setLoadingWeeklyMenu(true);
      }
      setMenuError(null);
      // Use augmented weekly to keep categorias e permitir múltiplos por dia sem misturar outros dias
      const weeklyData = await menuService.getWeeklyMenuAugmented(weekOffset);

      setWeeklyMenu(weeklyData);
      setRetryCount(0);
    } catch (error) {
      console.error('Error loading weekly menu:', error);
      setMenuError(error.message || 'Erro ao carregar cardápio semanal');
      
      // Use empty menu if database fails
      setWeeklyMenu({});
    } finally {
      if (showLoading) {
        setLoadingWeeklyMenu(false);
      }
    }
  }, []);

  // Toggle between daily and weekly view
  const toggleViewMode = (mode) => {
    setViewMode(mode);
    if (mode === 'weekly') {
      loadWeeklyMenu(true, currentWeekOffset);
    }
  };

  // Navegar entre semanas (reativado)
  const navigateWeek = (direction) => {
    const newWeekOffset = currentWeekOffset + direction;
    setCurrentWeekOffset(newWeekOffset);
    loadWeeklyMenu(true, newWeekOffset);
  };

  // Retry loading menu
  const retryLoadMenu = () => {
    setRetryCount(prev => prev + 1);
    if (viewMode === 'daily') {
      loadDailyMenu();
    } else {
      loadWeeklyMenu(true, currentWeekOffset);
    }
  };

  // Load menu when connection is established, with deduplication
  useEffect(() => {
    // If not connected, do not populate with fallback data
    if (!isConnected) {
      if (viewMode === 'daily') {
        setDailyMenu([]);
      } else if (viewMode === 'weekly') {
        setWeeklyMenu({});
      }
      return;
    }

    // Deduplicate initial fetch in StrictMode for daily view
    if (viewMode === 'daily') {
      if (hasFetchedRef.current) return;
      hasFetchedRef.current = true;
      loadDailyMenu();
    } else if (viewMode === 'weekly') {
      loadWeeklyMenu(true, currentWeekOffset);
    }
  }, [isConnected, loadDailyMenu, loadWeeklyMenu, viewMode, currentWeekOffset]);
  
  // Removed automatic menu reload - menu will update when user navigates or refreshes page
  
  // Reset deduplication when switching back to daily view
  useEffect(() => {
    if (viewMode === 'daily') {
      hasFetchedRef.current = false;
    }
  }, [viewMode]);

  const navigateDay = (direction) => {
    // Prevent navigation while loading or during cooldown
    if (loadingMenu || isNavigating) return;
    
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + direction);
    
    // Only allow navigation within the current month, unless admin
    if (currentUser?.isAdmin || (newDate.getMonth() === currentDate.getMonth() && newDate.getFullYear() === currentDate.getFullYear())) {
      setIsNavigating(true);
      setCurrentDate(newDate);
      loadDailyMenu(true, newDate);
      
      // Add cooldown period to prevent rapid clicking
      setTimeout(() => {
        setIsNavigating(false);
      }, 500); // 0.5 second cooldown
    }
  };

  const canNavigateDay = (direction) => {
    // Disable navigation while loading or during cooldown
    if (loadingMenu || isNavigating) return false;
    
    // Admins can always navigate
    if (currentUser?.isAdmin) return true;

    const testDate = new Date(currentDate);
    testDate.setDate(currentDate.getDate() + direction);
    return testDate.getMonth() === currentDate.getMonth() && testDate.getFullYear() === currentDate.getFullYear();
  };

  const canNavigateWeek = (direction) => {
    // Desabilita navegação enquanto carrega
    if (loadingWeeklyMenu) return false;

    // Admins can always navigate
    if (currentUser?.isAdmin) return true;

    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    // Offset da semana alvo
    const targetWeekOffset = currentWeekOffset + direction;

    // Início da semana atual (domingo)
    const currentDayOfWeek = today.getDay();
    const startOfCurrentWeek = new Date(today);
    startOfCurrentWeek.setDate(today.getDate() - currentDayOfWeek);
    startOfCurrentWeek.setHours(0, 0, 0, 0);

    // Semana alvo
    const startOfTargetWeek = new Date(startOfCurrentWeek);
    startOfTargetWeek.setDate(startOfCurrentWeek.getDate() + (targetWeekOffset * 7));
    const endOfTargetWeek = new Date(startOfTargetWeek);
    endOfTargetWeek.setDate(startOfTargetWeek.getDate() + 6);
    endOfTargetWeek.setHours(23, 59, 59, 999);

    // Limites do mês atual
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    lastDayOfMonth.setHours(23, 59, 59, 999);

    // Permite navegação somente enquanto a semana intersecta o mês atual
    return endOfTargetWeek >= firstDayOfMonth && startOfTargetWeek <= lastDayOfMonth;
  };

  const handleDateSelect = useCallback((selectedDate) => {
    setCurrentDate(selectedDate);
    loadDailyMenu(true, selectedDate);
  }, [loadDailyMenu]);

  const toggleCalendar = useCallback(() => {
    setIsCalendarOpen(!isCalendarOpen);
  }, [isCalendarOpen]);

  // Unit helpers and filter
  const getUnitId = (u) => {
    if (!u) return null;
    if (typeof u === 'string') return u;
    return u._id || u.id || u.key || null;
  };

  const getUnitName = (u) => {
    if (!u) return null;
    return u.name || u.displayName || (typeof u === 'string' ? u : null);
  };

  const belongsToUserUnit = (item) => {
    // If no logged user or user unit, do not filter
    if (!currentUser || !currentUser.unit) return true;
    const itemUnit = item.unit || item.unidade;
    if (!itemUnit) return false;

    const userId = getUnitId(currentUser.unit);
    const itemId = getUnitId(itemUnit);
    const userName = getUnitName(currentUser.unit);
    const itemName = getUnitName(itemUnit);

    if (userId && itemId) return String(itemId) === String(userId);
    if (userName && itemName) return String(itemName).toLowerCase() === String(userName).toLowerCase();
    return false;
  };

  // Filter menu items based on search and filters
  const filterMenuItems = (items) => {
    return items.filter(item => {
      // Search filter
      const matchesSearch = !searchTerm || 
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.descricao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.ingredients?.some(ing => ing.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Category filter
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      
      // Unit filter
      const matchesUnit = belongsToUserUnit(item);

      return matchesSearch && matchesCategory && matchesUnit && item.isAvailable;
    });
  };

  // Helpers: normalize categories, match synonyms/accents, and sort consistently
  const normalizeCategory = (value) => {
    if (!value) return '';
    return String(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };

  const categorySynonyms = {
    'prato principal': ['prato principal', 'prato-principal', 'principal', 'pratoprincipal'],
    'arroz': ['arroz'],
    'feijao': ['feijao'],
    'massa': ['massa', 'macarrao'],
    'salada': ['salada'],
    'guarnicao': ['guarnicao', 'acompanhamento'],
    'sobremesa': ['sobremesa', 'sobremesas', 'doce'],
    'suco': ['suco', 'sucos', 'bebida'],
    'entrada': ['entrada']
  };

  const matchCategory = (dish, targetCategory) => {
    const cat = normalizeCategory(dish.categoria || dish.category || dish.categoriaMenu);
    const target = normalizeCategory(targetCategory);
    const key = target;
    const allowed = categorySynonyms[key] || [target];
    return allowed.includes(cat);
  };

  const sortByOrderThenName = (a, b) => {
    const ao = a.ordem ?? a.order;
    const bo = b.ordem ?? b.order;
    if (ao != null && bo != null && ao !== bo) return ao - bo;
    const an = (a.nome || a.name || '').localeCompare((b.nome || b.name || ''), 'pt-BR');
    return an;
  };

  // Get filtered daily menu
  const getFilteredDailyMenu = () => {
    return filterMenuItems(dailyMenu || []);
  };

  // Handle menu item click
  const handleMenuItemClick = (item) => {
    setSelectedMenuItem(item);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMenuItem(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <div className="container mx-auto px-4 py-8 space-y-8">
        
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 bg-white dark:bg-slate-800 rounded-full px-6 py-3 shadow-lg mb-6">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Cardápio atualizado em tempo real
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-primary to-primary-dark bg-clip-text text-transparent mb-4">
            Nosso Cardápio
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Descubra sabores únicos com pratos especiais renovados diariamente
          </p>
        </div>

        

        {/* Menu Error Display */}
        {menuError && (
          <div className="bg-gradient-to-r from-yellow-100 to-primary/20 dark:from-yellow-900/30 dark:to-primary/20 border-l-4 border-yellow-500 rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Info className="text-yellow-600 dark:text-yellow-400 mr-3" size={24} weight="fill" />
                <div>
                  <h3 className="font-semibold text-yellow-800 dark:text-yellow-200">Aviso do Sistema</h3>
                  <p className="text-yellow-700 dark:text-yellow-300 text-sm mt-1">{menuError}</p>
                </div>
              </div>
              <button
                onClick={retryLoadMenu}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium"
                disabled={loadingMenu}
              >
                {loadingMenu ? '🔄 Tentando...' : `🔄 Tentar ${retryCount > 0 ? `(${retryCount})` : ''}`}
              </button>
            </div>
          </div>
        )}


        {/* Main Menu Section */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl overflow-hidden border border-slate-200 dark:border-slate-700">
          {/* Header */}
          <div className="bg-white dark:bg-slate-800 p-8 border-b border-slate-200 dark:border-slate-600">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="bg-gradient-to-br from-primary to-primary-dark p-3 rounded-2xl">
                    <Calendar className="w-8 h-8 text-white" weight="fill" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-slate-800 dark:text-white">
                      Cardápio Semanal
                    </h1>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-3 py-1 rounded-full text-sm font-medium">
                        ✅ Atualizado
                      </span>
                      <span className="text-slate-500 dark:text-slate-400 text-sm">
                        {new Date().toLocaleDateString('pt-BR', { 
                          weekday: 'long', 
                          day: 'numeric', 
                          month: 'long'
                        })}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-slate-600 dark:text-slate-300 mb-4">
                  Pratos especiais renovados diariamente para uma experiência gastronômica única
                </p>
                {/* <div className="flex flex-wrap gap-2">
                  <span className="bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-sm font-medium">
                    🔄 Rotação Automática
                  </span>
                  <span className="bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 px-3 py-1 rounded-full text-sm font-medium">
                    📱 Atualização Fácil
                  </span>
                  <span className="bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light px-3 py-1 rounded-full text-sm font-medium">
                    ⭐ Pratos Únicos
                  </span>
                </div> */}
              </div>
            </div>
          </div>

          {/* Daily Menu View */}
          <div className="p-8">
              {/* Date Selection - Only visible in daily mode */}
              {viewMode === 'daily' && (
                <div className="flex flex-col items-center mb-8 space-y-4">
                  {/* Calendar Date Picker */}
                  <div className="flex items-center justify-center">
                    <DatePicker
                      selectedDate={currentDate}
                      onDateSelect={handleDateSelect}
                      isOpen={isCalendarOpen}
                      onToggle={toggleCalendar}
                    />
                  </div>
                  
                  {/* Current Date Display */}
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
                      {currentDate.toLocaleDateString('pt-BR', { 
                        weekday: 'long', 
                        day: '2-digit', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </h2>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                      Cardápio do dia selecionado
                    </p>
                  </div>
                  
                  {/* Quick Day Navigation - Month Boundary Aware */}
                  <div className="flex items-center justify-center space-x-4">
                    <button
                      disabled={!canNavigateDay(-1)}
                      onClick={() => navigateDay(-1)}
                      className={`p-2 rounded-lg transition-all duration-200 group flex items-center space-x-2 ${
                        canNavigateDay(-1) 
                          ? 'hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer' 
                          : 'opacity-50 cursor-not-allowed'
                      }`}
                      title={loadingMenu || isNavigating ? "Carregando..." : (canNavigateDay(-1) ? "Dia anterior" : "Início do mês")}
                    >
                      <i className={`ph ph-caret-left text-lg ${
                        canNavigateDay(-1) ? 'group-hover:scale-110 transition-transform' : ''
                      }`}></i>
                      <span className="text-sm text-slate-600 dark:text-slate-400">Anterior</span>
                    </button>
                    
                    <button
                      disabled={!canNavigateDay(1)}
                      onClick={() => navigateDay(1)}
                      className={`p-2 rounded-lg transition-all duration-200 group flex items-center space-x-2 ${
                        canNavigateDay(1) 
                          ? 'hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer' 
                          : 'opacity-50 cursor-not-allowed'
                      }`}
                      title={loadingMenu || isNavigating ? "Carregando..." : (canNavigateDay(1) ? "Próximo dia" : "Fim do mês")}
                    >
                      <span className="text-sm text-slate-600 dark:text-slate-400">Próximo</span>
                      <i className={`ph ph-caret-right text-lg ${
                        canNavigateDay(1) ? 'group-hover:scale-110 transition-transform' : ''
                      }`}></i>
                    </button>
                  </div>
                </div>
              )}
              
              {/* View Mode Toggle */}
              <div className="flex justify-center mb-8">
                <div className="bg-slate-100 dark:bg-slate-700 rounded-xl p-1 flex">
                  <button
                    onClick={() => toggleViewMode('daily')}
                    className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                      viewMode === 'daily'
                        ? 'bg-primary text-white shadow-lg'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                    }`}
                  >
                    📅 Visualização Diária
                  </button>
                  <button
                    onClick={() => toggleViewMode('weekly')}
                    className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                      viewMode === 'weekly'
                        ? 'bg-primary text-white shadow-lg'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                    }`}
                  >
                    📋 Visualização Semanal
                  </button>
                </div>
              </div>
              
              {/* Daily Menu */}
              {viewMode === 'daily' && (
                <div>
                  {loadingMenu ? (
                    <div className="flex flex-col justify-center items-center py-20">
                      <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent mb-4"></div>
                      <span className="text-lg text-slate-600 dark:text-slate-300 font-medium">Carregando cardápio...</span>
                    </div>
                  ) : (
                    <div>
                      {/* Header com data atual */}
                      <div className="text-center mb-6">
                        <span className="bg-primary text-white px-3 py-1 rounded-full text-xs font-bold mb-2 inline-block">HOJE</span>
                        <div className="text-lg font-bold capitalize text-primary dark:text-primary-light">
                          {currentDate.toLocaleDateString('pt-BR', { weekday: 'long' })} - {currentDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                        </div>
                      </div>

                      {getFilteredDailyMenu().length > 0 ? (
                        (() => {
                          const dailyItems = getFilteredDailyMenu();
                          // Agrupar por categorias usando os helpers existentes
                          const pratoPrincipal = dailyItems.filter(d => matchCategory(d, 'prato principal')).sort(sortByOrderThenName);
                          const arroz = dailyItems.filter(d => matchCategory(d, 'arroz')).sort(sortByOrderThenName);
                          const feijao = dailyItems.filter(d => matchCategory(d, 'feijao')).sort(sortByOrderThenName);
                          const massa = dailyItems.filter(d => matchCategory(d, 'massa')).sort(sortByOrderThenName);
                          const salada = dailyItems.filter(d => matchCategory(d, 'salada')).sort(sortByOrderThenName);
                          const guarnicao = dailyItems.filter(d => matchCategory(d, 'guarnicao')).sort(sortByOrderThenName);
                          const sobremesas = dailyItems.filter(d => matchCategory(d, 'sobremesa')).sort(sortByOrderThenName);
                          const sucos = dailyItems.filter(d => matchCategory(d, 'suco')).sort(sortByOrderThenName);
                          const entrada = dailyItems.filter(d => matchCategory(d, 'entrada')).sort(sortByOrderThenName);

                          const categories = [
                            { key: 'prato-principal', label: '🍖 Prato Principal', items: pratoPrincipal },
                            { key: 'arroz', label: '🍚 Arroz', items: arroz },
                            { key: 'feijao', label: '🥘 Feijão', items: feijao },
                            { key: 'massa', label: '🍝 Massa', items: massa },
                            { key: 'salada', label: '🥗 Salada', items: salada },
                            { key: 'guarnicao', label: '🍛 Guarnições', items: guarnicao },
                            { key: 'sobremesa', label: '🍮 Sobremesas', items: sobremesas },
                            { key: 'suco', label: '🥤 Sucos', items: sucos },
                            { key: 'entrada', label: '🍽️ Entrada', items: entrada }
                          ];

                          return (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6">
                              {categories.filter(c => c.items.length > 0).map((cat) => (
                                <div key={cat.key} className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-700 dark:to-slate-800 rounded-xl p-6 border-2 border-slate-200 dark:border-slate-600 hover:shadow-lg transition-all duration-300">
                                  <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-bold text-slate-800 dark:text-white">{cat.label}</h3>
                                    {cat.items.length > 0 && (
                                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                        {cat.items.length}
                                      </span>
                                    )}
                                  </div>

                                  <div className="space-y-2">
                                    {cat.items.map((dish, idx) => (
                                      <div
                                        key={idx}
                                        className="text-slate-700 dark:text-slate-300 cursor-pointer hover:text-primary dark:hover:text-primary-light transition-colors p-2 rounded hover:bg-primary/10 dark:hover:bg-primary-dark/20 text-sm"
                                        onClick={() => handleMenuItemClick(dish)}
                                      >
                                        • {dish.nome || dish.name}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()
                      ) : (
                        <div className="text-center py-20">
                          <div className="text-6xl mb-4">🍽️</div>
                          <h3 className="text-xl font-bold text-slate-700 dark:text-slate-300 mb-2">Nenhum prato disponível hoje</h3>
                          <p className="text-slate-500 dark:text-slate-400">
                            {searchTerm || selectedCategory !== 'all' ? 'Tente ajustar os filtros de busca' : 'Volte amanhã para ver o cardápio do dia'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Weekly Menu */}
              {viewMode === 'weekly' && (
                <div className="space-y-8">
                  {loadingWeeklyMenu ? (
                    <div className="flex flex-col justify-center items-center py-20">
                      <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent mb-4"></div>
                      <span className="text-lg text-slate-600 dark:text-slate-300 font-medium">Carregando cardápio semanal...</span>
                    </div>
                  ) : (
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl overflow-hidden border border-slate-200 dark:border-slate-700">
                      
                      {Object.keys(weeklyMenu).length > 0 ? (
                        <div>

                          
                          {/* Header */}
                          <div className="bg-gradient-to-r from-primary to-primary-dark text-white p-4 md:p-6">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                              {/* Center Content - Mobile First */}
                              <div className="text-center order-1 md:order-2">
                                <h2 className="text-2xl md:text-3xl font-bold mb-2">Cardápio Semanal</h2>
                                <p className="text-primary-light text-sm md:text-base">Cardápio Regional</p>
                                <p className="text-primary-light text-xs md:text-sm">
                                  Itens selecionados por dia
                                </p>
                              </div>
                              
                              {/* Navegação semanal (limite: dentro do mês atual) */}
                              <div className="flex items-center justify-center gap-4 order-2 md:order-none">
                                <button
                                   onClick={() => navigateWeek(-1)}
                                   disabled={!canNavigateWeek(-1)}
                                   className={`flex items-center space-x-1 md:space-x-2 px-3 md:px-4 py-2 rounded-lg relative transition-all duration-200 ${
                                     canNavigateWeek(-1) 
                                       ? 'bg-white/20 hover:bg-white/30 cursor-pointer' 
                                       : 'bg-white/10 opacity-50 cursor-not-allowed'
                                   }`}
                                   style={{zIndex: 1000, position: 'relative'}}
                                   title={canNavigateWeek(-1) ? "Semana anterior" : "Início do mês"}
                                 >
                                   <CaretLeft className="text-base md:text-lg" />
                                   <span className="text-xs md:text-sm font-medium">Anterior</span>
                                 </button>
                                
                                <button
                                   onClick={() => navigateWeek(1)}
                                   disabled={!canNavigateWeek(1)}
                                   className={`flex items-center space-x-1 md:space-x-2 px-3 md:px-4 py-2 rounded-lg relative transition-all duration-200 ${
                                     canNavigateWeek(1) 
                                       ? 'bg-white/20 hover:bg-white/30 cursor-pointer' 
                                       : 'bg-white/10 opacity-50 cursor-not-allowed'
                                   }`}
                                   style={{zIndex: 1000, position: 'relative'}}
                                   title={canNavigateWeek(1) ? "Próxima semana" : "Fim do mês"}
                                 >
                                   <span className="text-xs md:text-sm font-medium">Próximo</span>
                                     <CaretRight className="text-base md:text-lg" />
                                   </button>
                              </div>
                            </div>
                          </div>

                          {/* Weekly Menu Cards */}
                          <div className="p-6 space-y-6">
                            {/* Check if there's any data for this week */}
                            {!Object.values(weeklyMenu).some(items => Array.isArray(items) && items.length > 0) ? (
                              <div className="text-center py-12">
                                <div className="bg-slate-100 dark:bg-slate-700 rounded-xl p-8">
                                  <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-2">
                                    Cardápio não disponível
                                  </h3>
                                  <p className="text-slate-600 dark:text-slate-400">
                                    Não há cardápio disponível para esta semana.
                                  </p>
                                </div>
                              </div>
                            ) : (
                              /* Order days correctly */
                              ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado']
                                .filter(day => weeklyMenu[day]) // Only include days that exist in the data
                                .map((day) => {
                              const dishes = weeklyMenu[day].sort((a, b) => {
                                if (!a.dataPreparacao && !b.dataPreparacao) return 0;
                                if (!a.dataPreparacao) return 1;
                                if (!b.dataPreparacao) return -1;
                                return new Date(b.dataPreparacao) - new Date(a.dataPreparacao);
                              });
                              const dayNames = {
                                'domingo': 'Domingo',
                                'segunda': 'Segunda-feira',
                                'terca': 'Terça-feira', 
                                'quarta': 'Quarta-feira',
                                'quinta': 'Quinta-feira',
                                'sexta': 'Sexta-feira',
                                'sabado': 'Sábado'
                              };
                              
                              const dayEmojis = {
                                'domingo': '☀️',
                                'segunda': '💼',
                                'terca': '🔥',
                                'quarta': '⚡',
                                'quinta': '🌟',
                                'sexta': '🎉',
                                'sabado': '🌙'
                              };
                              
                              // Calculate the correct date for each day of the week
                              const today = new Date();
                              const currentDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
                              
                              // Map day names to correct indices (0=domingo, 1=segunda, etc.)
                              const dayMap = {
                                'domingo': 0,
                                'segunda': 1, 
                                'terca': 2,
                                'quarta': 3,
                                'quinta': 4,
                                'sexta': 5,
                                'sabado': 6
                              };
                              
                              const dayIndex = dayMap[day];
                              
                              // Calculate the start of the current week (Sunday)
                              const startOfCurrentWeek = new Date(today);
                              startOfCurrentWeek.setDate(today.getDate() - currentDayOfWeek);
                              startOfCurrentWeek.setHours(0, 0, 0, 0);
                              
                              // Calculate the start of the target week based on weekOffset
                              const startOfTargetWeek = new Date(startOfCurrentWeek);
                              startOfTargetWeek.setDate(startOfCurrentWeek.getDate() + (currentWeekOffset * 7));
                              
                              // Calculate the specific day date in the target week
                              const dayDate = new Date(startOfTargetWeek);
                              dayDate.setDate(startOfTargetWeek.getDate() + dayIndex);
                              dayDate.setHours(0, 0, 0, 0); // Reset time to start of day
                              
                              // Get current month and year
                              const currentMonth = today.getMonth();
                              const currentYear = today.getFullYear();
                              // Limites do mês atual: mostra apenas dias dentro do mês (para não admins)
                              const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
                              const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
                              lastDayOfMonth.setHours(23, 59, 59, 999);

                              if (!currentUser?.isAdmin && (dayDate < firstDayOfMonth || dayDate > lastDayOfMonth)) {
                                return null;
                              }
                              
                              // Group dishes by category for this specific day
                              // Filtra também por dataPreparacao/specialDate para evitar itens de dias diferentes
                              
                              const pratoPrincipal = dishes.filter(dish => belongsToUserUnit(dish) && matchCategory(dish, 'prato principal')).sort(sortByOrderThenName);
                              const arroz = dishes.filter(dish => belongsToUserUnit(dish) && matchCategory(dish, 'arroz')).sort(sortByOrderThenName);
                              const feijao = dishes.filter(dish => belongsToUserUnit(dish) && matchCategory(dish, 'feijao')).sort(sortByOrderThenName);
                              const massa = dishes.filter(dish => belongsToUserUnit(dish) && matchCategory(dish, 'massa')).sort(sortByOrderThenName);
                              const salada = dishes.filter(dish => belongsToUserUnit(dish) && matchCategory(dish, 'salada')).sort(sortByOrderThenName);
                              const guarnicao = dishes.filter(dish => belongsToUserUnit(dish) && matchCategory(dish, 'guarnição')).sort(sortByOrderThenName);
                              const sobremesas = dishes.filter(dish => belongsToUserUnit(dish) && matchCategory(dish, 'sobremesa')).sort(sortByOrderThenName);
                              const sucos = dishes.filter(dish => belongsToUserUnit(dish) && matchCategory(dish, 'suco')).sort(sortByOrderThenName);
                              const entrada = dishes.filter(dish => belongsToUserUnit(dish) && matchCategory(dish, 'entrada')).sort(sortByOrderThenName);
                               const isToday = new Date().toDateString() === dayDate.toDateString();
                              return (
                                <div key={day} className={`bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-700 dark:to-slate-800 rounded-xl p-6 border-2 transition-all duration-300 hover:shadow-lg ${
                                  isToday ? 'border-primary' : 'border-slate-200 dark:border-slate-600'
                                }`}>
                                  {/* Day Header */}
                                  <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center space-x-3">
                                      <span className="text-2xl">{dayEmojis[day]}</span>
                                      <div>
                                        <h3 className={`text-xl font-bold ${
                                          isToday ? 'text-primary dark:text-primary-light' : 'text-slate-800 dark:text-white'
                                        }`}>
                                          {dayNames[day] || day}
                                        </h3>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">
                                          {dayDate.getDate()} de {dayDate.toLocaleDateString('pt-BR', { month: 'long' })}
                                        </p>
                                      </div>
                                    </div>
                                    {isToday && (
                                      <span className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                                        HOJE
                                      </span>
                                    )}
                                  </div>
                                  
                                  {/* Menu Categories Grid */}
                                  {dishes.length === 0 && (
                                    <div className="text-center py-12 bg-slate-50 dark:bg-slate-700/30 rounded-lg border border-dashed border-slate-300 dark:border-slate-600">
                                      <p className="text-slate-500 dark:text-slate-400 font-medium">Cardápio indisponível</p>
                                    </div>
                                  )}
                                  <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 ${dishes.length === 0 ? 'hidden' : ''}`}>
                                    {/* Prato Principal */}
                                    {pratoPrincipal.length > 0 && (
                                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                                        <div className="flex items-center space-x-2 mb-3">
                                          <span className="text-lg">🍽️</span>
                                          <h4 className="font-semibold text-slate-800 dark:text-white">Prato Principal</h4>
                                          {pratoPrincipal.length > 1 && (
                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                              {pratoPrincipal.length}
                                            </span>
                                          )}
                                        </div>
                                        <div className="space-y-2">
                                          {pratoPrincipal.map((dish, idx) => (
                                            <div 
                                              key={idx} 
                                              className="text-slate-700 dark:text-slate-300 cursor-pointer hover:text-primary dark:hover:text-primary-light transition-colors p-2 rounded hover:bg-primary/10 dark:hover:bg-primary-dark/20 text-sm"
                                              onClick={() => handleMenuItemClick(dish)}
                                            >
                                              • {dish.nome}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Arroz */}
                                    {arroz.length > 0 && (
                                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                                        <div className="flex items-center space-x-2 mb-3">
                                          <span className="text-lg">🍚</span>
                                          <h4 className="font-semibold text-slate-800 dark:text-white">Arroz</h4>
                                          {arroz.length > 1 && (
                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                              {arroz.length}
                                            </span>
                                          )}
                                        </div>
                                        <div className="space-y-2">
                                          {arroz.map((dish, idx) => (
                                            <div 
                                              key={idx} 
                                              className="text-slate-700 dark:text-slate-300 cursor-pointer hover:text-primary dark:hover:text-primary-light transition-colors p-2 rounded hover:bg-primary/10 dark:hover:bg-primary-dark/20 text-sm"
                                              onClick={() => handleMenuItemClick(dish)}
                                            >
                                              • {dish.nome}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Feijão */}
                                    {feijao.length > 0 && (
                                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                                        <div className="flex items-center space-x-2 mb-3">
                                          <span className="text-lg">🥘</span>
                                          <h4 className="font-semibold text-slate-800 dark:text-white">Feijão</h4>
                                          {feijao.length > 1 && (
                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                              {feijao.length}
                                            </span>
                                          )}
                                        </div>
                                        <div className="space-y-2">
                                          {feijao.map((dish, idx) => (
                                            <div 
                                              key={idx} 
                                              className="text-slate-700 dark:text-slate-300 cursor-pointer hover:text-primary dark:hover:text-primary-light transition-colors p-2 rounded hover:bg-primary/10 dark:hover:bg-primary-dark/20 text-sm"
                                              onClick={() => handleMenuItemClick(dish)}
                                            >
                                              • {dish.nome}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Massa */}
                                    {massa.length > 0 && (
                                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                                        <div className="flex items-center space-x-2 mb-3">
                                          <span className="text-lg">🍝</span>
                                          <h4 className="font-semibold text-slate-800 dark:text-white">Massa</h4>
                                          {massa.length > 1 && (
                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                              {massa.length}
                                            </span>
                                          )}
                                        </div>
                                        <div className="space-y-2">
                                          {massa.map((dish, idx) => (
                                            <div 
                                              key={idx} 
                                              className="text-slate-700 dark:text-slate-300 cursor-pointer hover:text-primary dark:hover:text-primary-light transition-colors p-2 rounded hover:bg-primary/10 dark:hover:bg-primary-dark/20 text-sm"
                                              onClick={() => handleMenuItemClick(dish)}
                                            >
                                              • {dish.nome}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Salada */}
                                    {salada.length > 0 && (
                                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                                        <div className="flex items-center space-x-2 mb-3">
                                          <span className="text-lg">🥗</span>
                                          <h4 className="font-semibold text-slate-800 dark:text-white">Salada</h4>
                                          {salada.length > 1 && (
                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                              {salada.length}
                                            </span>
                                          )}
                                        </div>
                                        <div className="space-y-2">
                                          {salada.map((dish, idx) => (
                                            <div 
                                              key={idx} 
                                              className="text-slate-700 dark:text-slate-300 cursor-pointer hover:text-primary dark:hover:text-primary-light transition-colors p-2 rounded hover:bg-primary/10 dark:hover:bg-primary-dark/20 text-sm"
                                              onClick={() => handleMenuItemClick(dish)}
                                            >
                                              • {dish.nome}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Guarnição */}
                                    {guarnicao.length > 0 && (
                                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                                        <div className="flex items-center space-x-2 mb-3">
                                          <span className="text-lg">🥕</span>
                                          <h4 className="font-semibold text-slate-800 dark:text-white">Guarnição</h4>
                                          {guarnicao.length > 1 && (
                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                              {guarnicao.length}
                                            </span>
                                          )}
                                        </div>
                                        <div className="space-y-2">
                                          {guarnicao.map((dish, idx) => (
                                            <div 
                                              key={idx} 
                                              className="text-slate-700 dark:text-slate-300 cursor-pointer hover:text-primary dark:hover:text-primary-light transition-colors p-2 rounded hover:bg-primary/10 dark:hover:bg-primary-dark/20 text-sm"
                                              onClick={() => handleMenuItemClick(dish)}
                                            >
                                              • {dish.nome}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Sobremesas */}
                                    {sobremesas.length > 0 && (
                                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                                        <div className="flex items-center space-x-2 mb-3">
                                          <span className="text-lg">🍰</span>
                                          <h4 className="font-semibold text-slate-800 dark:text-white">Sobremesas</h4>
                                          {sobremesas.length > 1 && (
                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                              {sobremesas.length}
                                            </span>
                                          )}
                                        </div>
                                        <div className="space-y-2">
                                          {sobremesas.map((dish, idx) => (
                                            <div 
                                              key={idx} 
                                              className="text-slate-700 dark:text-slate-300 cursor-pointer hover:text-primary dark:hover:text-primary-light transition-colors p-2 rounded hover:bg-primary/10 dark:hover:bg-primary-dark/20 text-sm"
                                              onClick={() => handleMenuItemClick(dish)}
                                            >
                                              • {dish.nome}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Sucos */}
                                    {sucos.length > 0 && (
                                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                                        <div className="flex items-center space-x-2 mb-3">
                                          <span className="text-lg">🧃</span>
                                          <h4 className="font-semibold text-slate-800 dark:text-white">Sucos</h4>
                                          {sucos.length > 1 && (
                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                              {sucos.length}
                                            </span>
                                          )}
                                        </div>
                                        <div className="space-y-2">
                                          {sucos.map((dish, idx) => (
                                            <div 
                                              key={idx} 
                                              className="text-slate-700 dark:text-slate-300 cursor-pointer hover:text-primary dark:hover:text-primary-light transition-colors p-2 rounded hover:bg-primary/10 dark:hover:bg-primary-dark/20 text-sm"
                                              onClick={() => handleMenuItemClick(dish)}
                                            >
                                              • {dish.nome}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Entrada */}
                                    {entrada.length > 0 && (
                                      <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                                        <div className="flex items-center space-x-2 mb-3">
                                          <span className="text-lg">🥙</span>
                                          <h4 className="font-semibold text-slate-800 dark:text-white">Entrada</h4>
                                          {entrada.length > 1 && (
                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                              {entrada.length}
                                            </span>
                                          )}
                                        </div>
                                        <div className="space-y-2">
                                          {entrada.map((dish, idx) => (
                                            <div 
                                              key={idx} 
                                              className="text-slate-700 dark:text-slate-300 cursor-pointer hover:text-primary dark:hover:text-primary-light transition-colors p-2 rounded hover:bg-primary/10 dark:hover:bg-primary-dark/20 text-sm"
                                              onClick={() => handleMenuItemClick(dish)}
                                            >
                                              • {dish.nome}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-20">
                          <div className="text-6xl mb-4">📅</div>
                          <h3 className="text-xl font-bold text-slate-700 dark:text-slate-300 mb-2">
                            Cardápio semanal não disponível
                          </h3>
                          <p className="text-slate-500 dark:text-slate-400">
                            Tente novamente em alguns instantes
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
        </div>

        {/* Menu Item Details Modal */}
        {isModalOpen && selectedMenuItem && (
          <div className="fixed inset-0 z-[100]">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={closeModal} />
            {/* Modal Wrapper */}
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <div 
                className="bg-gradient-to-br from-white via-slate-50/90 to-white dark:from-slate-800 dark:via-slate-700/90 dark:to-slate-800 rounded-3xl shadow-2xl shadow-slate-900/20 dark:shadow-black/40 border border-slate-200/50 dark:border-slate-600/50 max-w-2xl w-full max-h-[90vh] overflow-y-auto transform transition-all duration-300 scale-100 hover:scale-[1.01]"
                onClick={(e) => e.stopPropagation()}
              >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-8 rounded-t-3xl bg-gradient-to-r from-primary to-primary-dark text-white">
                <h3 className="text-2xl font-bold text-white drop-shadow-sm">
                  {selectedMenuItem.name || selectedMenuItem.nome}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
                >
                  <X size={24} className="text-white" />
                </button>
              </div>
              
              {/* Modal Content */}
              <div className="p-8 space-y-6">

                {/* Image */}
                {(selectedMenuItem.image || selectedMenuItem.imagem) && (
                  <div className="flex justify-center">
                    <img
                      src={selectedMenuItem.image || selectedMenuItem.imagem}
                      alt={(selectedMenuItem.name || selectedMenuItem.nome) + ' - imagem do prato'}
                      className="w-80 h-60 sm:w-96 sm:h-72 object-cover rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 bg-white"
                    />
                  </div>
                )}

                
                {/* Description */}
                {(selectedMenuItem.description || selectedMenuItem.descricao) && (
                  <div className="bg-slate-50 dark:bg-slate-700/30 p-4 rounded-2xl border-l-4 border-primary/70">
                    <h4 className="font-bold text-slate-800 dark:text-white mb-3">Descrição</h4>
                    <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                      {selectedMenuItem.description || selectedMenuItem.descricao}
                    </p>
                  </div>
                )}

                {/* Unit Information */}
                {(selectedMenuItem.unidade?.name || selectedMenuItem.unit?.name) && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-2xl border-l-4 border-amber-400/80">
                    <h4 className="font-bold text-slate-800 dark:text-white mb-3">Unidade</h4>
                    <p className="text-slate-700 dark:text-slate-300">
                      {selectedMenuItem.unidade?.name || selectedMenuItem.unit?.name}
                    </p>
                  </div>
                )}
                

                
                {/* Category */}
                {selectedMenuItem.category && (
                  <div>
                    <h4 className="font-bold text-slate-800 dark:text-white mb-3">Categoria</h4>
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-400 to-green-500 text-white shadow-sm font-medium">
                      🏷️ {selectedMenuItem.category}
                    </span>
                  </div>
                )}
              </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cardapio;